import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../../stores/authStore';
import { AccentButton } from '../components/Button';
import Input from '../components/Input';
import ThemeSwitch from '../components/ThemeSwitch';
import { handleApiErrorWithMessages } from '../../utils/apiErrorHandler';

const SignInPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const { login } = useAuthStore();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    setGeneralError(null);
    clearErrors();
    
    try {
      const success = await login(data.email, data.password);
      if (success) {
        navigate('/my');
      }
    } catch (error) {
      const { generalError: apiGeneralError, fieldErrors } = handleApiErrorWithMessages(error);
      
      // Set field-specific errors
      Object.keys(fieldErrors).forEach((field) => {
        setError(field, {
          type: 'server',
          message: fieldErrors[field],
        });
      });
      
      // Set general error if present
      if (apiGeneralError) {
        setGeneralError(apiGeneralError);
      } else if (Object.keys(fieldErrors).length === 0) {
        setGeneralError('Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#121212] py-12 px-4 sm:px-6 relative">
      {/* Theme Switch - Top Right */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6">
        <ThemeSwitch />
      </div>
      
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-[#121212] dark:text-[#D0D0D0]">
            Sign in to your account
          </h1>
          <p className="mt-2 text-sm text-[#7B7B7B] dark:text-[#848484]">
            Don't have an account?{' '}
            <Link
              to="/sign-up"
              className="font-medium text-[#121212] dark:text-[#D0D0D0] hover:underline"
            >
              Sign up
            </Link>
          </p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {/* General Error Display */}
          {generalError && (
            <div className="rounded-xl border border-red-500 bg-red-50 dark:bg-red-900/20 p-4">
              <p className="text-sm text-red-600 dark:text-red-400">{generalError}</p>
            </div>
          )}
          
          <div className="space-y-4">
            {/* Email Field */}
            <Input
              type="email"
              label="Email"
              placeholder="Enter email"
              autoComplete="email"
              error={errors.email?.message}
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
            />

            {/* Password Field */}
            <Input
              type="password"
              label="Password"
              placeholder="Enter password"
              autoComplete="current-password"
              showPasswordToggle={true}
              error={errors.password?.message}
              {...register('password', {
                required: 'Password is required',
              })}
            />
          </div>

          {/* Forgot Password Link */}
          <div className="flex items-center justify-end">
            <Link
              to="/password-reset-request"
              className="text-sm text-[#7B7B7B] dark:text-[#848484] hover:text-[#121212] dark:hover:text-[#D0D0D0] hover:underline"
            >
              Forgot password?
            </Link>
          </div>

          {/* Submit Button */}
          <div>
            <AccentButton
              type="submit"
              variant="primary"
              disabled={isLoading}
              className="w-full h-[48px]"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </AccentButton>
          </div>

          {/* OR Separator */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#ECECEC] dark:border-[#202020]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-[#121212] text-[#7B7B7B] dark:text-[#848484]">OR</span>
            </div>
          </div>

          {/* Short Code Login Button */}
          <div>
            <AccentButton
              type="button"
              variant="secondary"
              onClick={() => navigate('/short-code-request')}
              className="w-full h-[38px]"
            >
              Email sign-in code
            </AccentButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignInPage;
